import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Building2,
  Users,
  Phone,
  Wifi,
  TrendingUp,
  UserPlus,
  Copy,
  RefreshCw,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Sparkles,
  MapPin,
  Mail,
  Calendar,
  Briefcase,
  Globe,
  User,
  Building,
  Database,
} from 'lucide-react';
import { formatarCNPJ } from '@/utils/cnpj';
import { formatarCPF } from '@/utils/formatters';
import { useDossieCnpj } from '@/hooks/useDossieCnpj';
import { useCoberturaPorCeps } from '@/hooks/useCepCoberturaClaroBl';
import { useCriarLeadDoEcossistema } from '@/hooks/useCriarLeadDoEcossistema';
import { useToast } from '@/hooks/use-toast';
import type { CascataStatus, EcossistemaConsultaResult } from '@/hooks/useEcossistemaApi';
import { cn } from '@/lib/utils';

// ============================================================================
// DossieCnpjCard
// ============================================================================
// Layout: 1 coluna esquerda fixa + 2 colunas à direita.
// Princípios visuais:
//   1. Badge da fonte da informação aparece UMA vez no header de cada bloco,
//      não em cada campo (evita poluição visual).
//   2. Cobertura BL sempre mostra o endereço junto.
//   3. Telefones consolidados garante mostrar tudo mesmo quando origem não veio.
//   4. Sem botão "Ver Receita completa" — campos Receita já estão expostos.
//   5. Score recalculado no frontend como fallback se o RPC retornou 0.
// ============================================================================

interface DossieCnpjCardProps {
  cnpj: string;
  consultaId?: string;
  cascataStatus?: CascataStatus;
  orquestradorData?: EcossistemaConsultaResult | null;
  onReEnriquecer?: () => void;
}

// ── Badge de fonte (usado só no header dos blocos) ─────────────────────────
type Fonte = 'receita' | 'assertiva-cnpj' | 'assertiva-cpf' | 'crm' | 'operadora';

function FonteBadge({ fonte }: { fonte: Fonte }) {
  const map: Record<Fonte, { label: string; className: string }> = {
    receita: { label: 'Receita Federal', className: 'bg-info/10 text-info border-info/30' },
    'assertiva-cnpj': { label: 'Assertiva CNPJ', className: 'bg-primary/10 text-primary border-primary/30' },
    'assertiva-cpf': { label: 'Assertiva CPF', className: 'bg-primary/15 text-primary border-primary/40' },
    crm: { label: 'CRM', className: 'bg-warning/10 text-warning border-warning/30' },
    operadora: { label: 'API Operadora', className: 'bg-success/10 text-success border-success/30' },
  };
  const cfg = map[fonte];
  return (
    <Badge variant="outline" className={cn('text-[10px] py-0 h-4 px-1.5', cfg.className)}>
      {cfg.label}
    </Badge>
  );
}

// ── Field compacto sem badge (badge fica no header da seção) ───────────────
function Field({ label, value, mono }: { label: string; value: any; mono?: boolean }) {
  if (value === null || value === undefined || value === '' || value === 0) return null;
  return (
    <div className="text-xs space-y-0.5">
      <div className="text-muted-foreground text-[10px] uppercase tracking-wider">{label}</div>
      <div className={cn('font-medium', mono && 'font-mono')}>{value}</div>
    </div>
  );
}

// ── Status da cascata em linha ──────────────────────────────────────────────
function StatusSelo({ status, label }: { status?: string; label: string }) {
  let icon = AlertCircle;
  let cls = 'bg-muted text-muted-foreground';
  if (status === 'ok') {
    icon = CheckCircle2;
    cls = 'bg-success/10 text-success border-success/20';
  } else if (status === 'erro') {
    icon = XCircle;
    cls = 'bg-danger/10 text-danger border-danger/20';
  } else if (
    status === 'sem_telefones' ||
    status === 'sem_cep' ||
    status === 'tabela_indisponivel' ||
    status === 'not_found'
  ) {
    icon = AlertCircle;
    cls = 'bg-warning/10 text-warning border-warning/20';
  }
  const Icon = icon;
  return (
    <Badge variant="outline" className={cn('gap-1 text-[10px] py-0 h-5', cls)}>
      <Icon className="h-3 w-3" />
      {label}
    </Badge>
  );
}

// ── Selo de cobertura por CEP — sempre com referência ao endereço ──────────
function CoberturaCepBadge({
  cep,
  coberturaPorCep,
}: {
  cep?: string | null;
  coberturaPorCep?: Record<string, { ativo: boolean; tipo_servico?: string | null; velocidade_max_mbps?: number | null }> | null;
}) {
  if (!cep || !coberturaPorCep) return null;
  const cepClean = String(cep).replace(/\D/g, '');
  const item = coberturaPorCep[cepClean];
  if (!item) return null;
  if (item.ativo) {
    const t = String(item.tipo_servico || '').toUpperCase();
    const isFibra = t === 'FIBRA';
    const isHfc = t === 'HFC';
    const cls = isFibra
      ? 'bg-success/15 border-success/40 text-success font-medium'
      : isHfc
      ? 'bg-brand-mid/10 border-brand-mid/30 text-brand-mid'
      : 'bg-muted border-border text-muted-foreground';
    const label = isFibra ? 'Fibra · Claro BL' : isHfc ? 'HFC · coax' : 'Cobertura BL';
    return (
      <Badge variant="outline" className={`text-[10px] py-0 h-5 gap-1 ${cls}`}>
        <Wifi className="h-2.5 w-2.5" />
        {label}
        {item.velocidade_max_mbps ? ` · ${item.velocidade_max_mbps} Mbps` : ''}
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-[10px] py-0 h-5 bg-danger/10 border-danger/30 text-danger">
      Sem cobertura
    </Badge>
  );
}

// ────────────────────────────────────────────────────────────────────────────
export function DossieCnpjCard({
  cnpj,
  consultaId,
  cascataStatus,
  orquestradorData,
  onReEnriquecer,
}: DossieCnpjCardProps) {
  const { data: dossieView } = useDossieCnpj(cnpj);
  const { mutate: criarLead, isPending: criandoLead } = useCriarLeadDoEcossistema();
  const { toast } = useToast();

  const data = orquestradorData;
  const r = data?.receita || {};
  const est = r.estabelecimento || {};
  const a = data?.assertiva || {};
  const ac = a.dadosCadastrais || {};
  const sociosEnr = data?.socios_enriquecidos || [];
  const telefonesOrigem = data?.telefones_origem || {};
  const operadoras = data?.operadoras || [];

  // ── Coleta TODOS os CEPs encontrados (matriz + filiais + sócios) ────────
  // Lógica: se tem endereço, consulta cobertura. Não importa de onde veio o
  // endereço — a tabela cep_cobertura_claro_bl é só CEP → ativo, sem CNPJ.
  const enderecosPorCep = useMemo(() => {
    const mapa: Record<string, { contexto: string; logradouro: string; isPrincipal: boolean }> = {};
    const addEndereco = (cep: any, contexto: string, logradouroPartes: any[], isPrincipal = false) => {
      const c = String(cep || '').replace(/\D/g, '');
      if (c.length !== 8) return;
      const logradouro = logradouroPartes.filter(Boolean).join(' ').trim();
      // Se já existe e o novo é principal, sobrescreve para garantir destaque
      if (!mapa[c] || isPrincipal) {
        mapa[c] = { contexto, logradouro, isPrincipal: isPrincipal || mapa[c]?.isPrincipal || false };
      }
    };

    // Receita Federal — endereço da matriz (PRINCIPAL)
    if (est.cep) {
      addEndereco(
        est.cep,
        'Matriz (Receita)',
        [
          est.tipo_logradouro,
          est.logradouro,
          est.numero ? `, ${est.numero}` : '',
          est.bairro ? ` · ${est.bairro}` : '',
          est.cidade?.nome ? ` · ${est.cidade.nome}/${est.estado?.sigla || ''}` : '',
        ],
        true
      );
    }

    // Endereços Assertiva CNPJ
    for (const [i, end] of (a.enderecos || []).entries()) {
      addEndereco(
        end?.cep,
        `Endereço ${i + 1} (Assertiva CNPJ)`,
        [
          end.logradouro,
          end.numero ? `, ${end.numero}` : '',
          end.bairro ? ` · ${end.bairro}` : '',
          end.cidade ? ` · ${end.cidade}/${end.uf || ''}` : '',
        ]
      );
    }

    // Endereços de cada sócio (Assertiva CPF)
    for (const item of sociosEnr) {
      const e = item.enriquecimento || {};
      const dc = e.dadosCadastrais || {};
      const nomeSocio =
        dc.nome ||
        item.socio_original?.nomeOuRazaoSocial ||
        item.socio_original?.nome ||
        'Sócio';
      const enderecos = e.enderecos || (e.endereco ? [e.endereco] : []);
      for (const end of enderecos) {
        addEndereco(
          end?.cep,
          `Sócio: ${nomeSocio}`,
          [
            end.logradouro,
            end.numero ? `, ${end.numero}` : '',
            end.bairro ? ` · ${end.bairro}` : '',
            end.cidade ? ` · ${end.cidade}/${end.uf || ''}` : '',
          ]
        );
      }
    }

    return mapa;
  }, [est, a.enderecos, sociosEnr]);

  const cepsList = useMemo(() => Object.keys(enderecosPorCep), [enderecosPorCep]);

  // Consulta direta no banco — INDEPENDENTE da edge orquestradora
  const { data: coberturaPorCepDireto } = useCoberturaPorCeps(cepsList);

  // Mesclagem: prioriza dado direto do banco; fallback para o que veio
  // do orquestrador (compatibilidade).
  const coberturaPorCep = useMemo(() => {
    const fallback = data?.cobertura_por_cep || {};
    const direto = coberturaPorCepDireto || {};
    return { ...fallback, ...direto };
  }, [data?.cobertura_por_cep, coberturaPorCepDireto]);

  const cnpjStr = data?.cnpj || cnpj;
  const razaoSocial = r.razao_social || ac.razaoSocial || dossieView?.razao_social || 'Sem razão social';

  // Score: fonte ÚNICA é o cálculo do banco (função calcular_score_dossie
  // chamada pela edge OU pela VIEW vw_dossie_cnpj). Frontend não recalcula —
  // se vier 0, o motivo está nos sinais reais ou na função SQL precisa ser
  // ajustada lá, não aqui.
  const score = data?.score ?? dossieView?.score ?? 0;
  const isLeadQuente = score >= 60;

  // ── Cobertura: estatística baseada em TODOS os endereços encontrados ────
  // Combina lista de endereços (enderecosPorCep) com resultado da consulta
  // (coberturaPorCep). O endereço PRINCIPAL (matriz da Receita) sempre vem
  // primeiro na lista para destaque.
  const coberturaStats = useMemo(() => {
    type Item = {
      cep: string;
      ativo: boolean;
      contexto: string;
      logradouro: string;
      isPrincipal: boolean;
      tipo?: string | null;
      vel?: number | null;
    };
    const lista: Item[] = Object.entries(enderecosPorCep).map(([cep, end]) => {
      const cob = (coberturaPorCep as any)[cep];
      return {
        cep,
        ativo: !!cob?.ativo,
        contexto: end.contexto,
        logradouro: end.logradouro,
        isPrincipal: end.isPrincipal,
        tipo: cob?.tipo_servico,
        vel: cob?.velocidade_max_mbps,
      };
    });
    // Principal sempre primeiro
    lista.sort((a, b) => (a.isPrincipal ? -1 : b.isPrincipal ? 1 : 0));
    return {
      total: lista.length,
      comCobertura: lista.filter((x) => x.ativo).length,
      lista,
      principal: lista.find((x) => x.isPrincipal) || null,
    };
  }, [enderecosPorCep, coberturaPorCep]);

  // ── Telefones agrupados por origem (com fallback robusto) ──────────────
  const telefonesGrupos = useMemo(() => {
    const grupos = {
      empresa: [] as Array<{ numero: string; operadora: string; portabilidade: string; detalhe?: string }>,
      socios: [] as Array<{ numero: string; operadora: string; portabilidade: string; detalhe?: string }>,
      crm: [] as Array<{ numero: string; operadora: string; portabilidade: string; detalhe?: string }>,
      semOrigem: [] as Array<{ numero: string; operadora: string; portabilidade: string; detalhe?: string }>,
    };
    for (const op of operadoras) {
      const numero = String(op['NÚMERO'] || '');
      const origem = telefonesOrigem[numero];
      const item = {
        numero,
        operadora: String(op['OPERADORA'] || ''),
        portabilidade: String(op['DATA PORTABILIDADE'] || ''),
        detalhe: origem?.detalhe,
      };
      if (origem?.origem === 'empresa') grupos.empresa.push(item);
      else if (origem?.origem === 'socio') grupos.socios.push(item);
      else if (origem?.origem === 'crm') grupos.crm.push(item);
      else grupos.semOrigem.push(item);
    }
    return grupos;
  }, [operadoras, telefonesOrigem]);

  const totalTelefones = operadoras.length;

  // Melhor telefone para o lead (celular não-Claro)
  const melhorTelefone = useMemo(() => {
    const naoClaro = operadoras.find((o: any) => {
      const op = String(o.OPERADORA || '').toUpperCase();
      const digits = String(o['NÚMERO'] || '').replace(/\D/g, '');
      return digits.length >= 12 && !['CLARO', 'ERRO', 'DESCONHECIDO', 'NÃO LOCALIZADO'].includes(op);
    });
    return (naoClaro?.['NÚMERO'] as string) || (operadoras[0]?.['NÚMERO'] as string) || null;
  }, [operadoras]);

  const handleCriarLead = () => {
    // consultaId é OPCIONAL agora — o lead é criado mesmo sem persist da
    // consulta (caso PGRST205 ou similar). Apenas a flag virou_lead na
    // ecossistema_consultas não atualiza, o que é tolerável.
    criarLead({
      consultaId: consultaId || null,
      cnpj: cnpjStr,
      razaoSocial,
      uf: est.estado?.sigla || null,
      telefone: melhorTelefone,
      email: est.email || a.emails?.[0]?.email || null,
    });
  };

  const handleCopiar = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
      toast({ title: 'Dossiê copiado para clipboard' });
    } catch {
      toast({ title: 'Falha ao copiar', variant: 'destructive' });
    }
  };

  if (!data) {
    return (
      <Card>
        <CardContent className="py-6 text-sm text-muted-foreground">Sem dados ainda.</CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
      {/* ────────────── COLUNA ESQUERDA (sticky) ────────────── */}
      <aside className="lg:col-span-4 xl:col-span-3 space-y-3 lg:sticky lg:top-2 lg:self-start">
        {/* Header com score */}
        <Card className={cn('border-2', isLeadQuente && 'border-warning/40 bg-warning/5')}>
          <CardContent className="py-3 px-4">
            <div className="flex items-start gap-2 mb-2">
              <Building2 className="h-5 w-5 text-primary mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm leading-tight line-clamp-2">{razaoSocial}</div>
                <div className="text-xs text-muted-foreground font-mono mt-1">{formatarCNPJ(cnpjStr)}</div>
              </div>
            </div>
            <div className="flex items-center gap-3 mt-2">
              <div className="text-center">
                <div className={cn('text-3xl font-bold leading-none', isLeadQuente ? 'text-warning' : 'text-muted-foreground')}>
                  {score}
                </div>
                <div className="text-[10px] text-muted-foreground">/100</div>
              </div>
              <div className="space-y-1 flex-1 min-w-0">
                {isLeadQuente && (
                  <Badge className="bg-warning hover:bg-warning gap-1 text-[10px]">
                    <TrendingUp className="h-2.5 w-2.5" /> Lead Quente
                  </Badge>
                )}
                {est.situacao_cadastral && (
                  <Badge variant="outline" className="text-[10px] py-0 h-4 block w-fit">
                    {est.situacao_cadastral}
                  </Badge>
                )}
                {r.porte?.descricao && <div className="text-[10px] text-muted-foreground">{r.porte.descricao}</div>}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Cobertura BL — endereço principal destacado + lista de demais */}
        <Card
          className={cn(
            'border-2',
            coberturaStats.principal?.ativo && 'border-success/40 bg-success/5',
            coberturaStats.principal && !coberturaStats.principal.ativo && 'border-danger/40 bg-danger/5',
            !coberturaStats.principal && coberturaStats.comCobertura > 0 && 'border-success/40 bg-success/5'
          )}
        >
          <CardHeader className="pb-1 pt-3 px-4">
            <CardTitle className="text-xs flex items-center gap-1.5">
              <Wifi className="h-3.5 w-3.5" /> Cobertura Claro BL
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 px-4 pb-3 space-y-2">
            {coberturaStats.total === 0 ? (
              <div className="text-[11px] text-muted-foreground">
                Nenhum endereço para verificar.
              </div>
            ) : (
              <>
                {/* PRINCIPAL DESTACADO — Matriz da Receita */}
                {coberturaStats.principal && (
                  <div
                    className={cn(
                      'rounded-md p-2 border-2',
                      coberturaStats.principal.ativo
                        ? 'bg-success/10 border-success/40'
                        : 'bg-danger/10 border-danger/40'
                    )}
                  >
                    <div className="text-[10px] uppercase tracking-wider font-semibold mb-1 flex items-center gap-1">
                      <MapPin className="h-2.5 w-2.5" />
                      Endereço principal — {coberturaStats.principal.contexto}
                    </div>
                    {coberturaStats.principal.logradouro && (
                      <div className="text-[11px] font-medium leading-tight">
                        {coberturaStats.principal.logradouro}
                      </div>
                    )}
                    <div className="text-[10px] font-mono text-muted-foreground mt-0.5">
                      CEP {coberturaStats.principal.cep}
                    </div>
                    <div
                      className={cn(
                        'text-xs font-bold mt-1.5 flex items-center gap-1',
                        coberturaStats.principal.ativo ? 'text-success' : 'text-danger'
                      )}
                    >
                      {coberturaStats.principal.ativo ? (
                        <>
                          <CheckCircle2 className="h-3 w-3" /> COM COBERTURA
                          {coberturaStats.principal.tipo && (
                            <span className="font-normal">· {coberturaStats.principal.tipo}</span>
                          )}
                          {coberturaStats.principal.vel && (
                            <span className="font-normal">· {coberturaStats.principal.vel} Mbps</span>
                          )}
                        </>
                      ) : (
                        <>
                          <XCircle className="h-3 w-3" /> SEM COBERTURA
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Resumo */}
                <div className="text-[11px] text-muted-foreground">
                  Total: <b className="text-foreground">{coberturaStats.comCobertura}</b> de{' '}
                  <b className="text-foreground">{coberturaStats.total}</b> endereço(s) com cobertura
                </div>

                {/* Lista dos demais endereços (não-principais) */}
                {coberturaStats.lista.filter((x) => !x.isPrincipal).length > 0 && (
                  <div className="space-y-1.5 border-t pt-2">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      Outros endereços
                    </div>
                    {coberturaStats.lista
                      .filter((x) => !x.isPrincipal)
                      .map((c, i) => (
                        <div key={i} className="text-[11px] py-1 border-t border-border/40 first:border-0 first:pt-0">
                          <div className="flex items-start gap-1.5">
                            <MapPin className="h-3 w-3 text-muted-foreground mt-0.5 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="text-[10px] text-muted-foreground">{c.contexto}</div>
                              {c.logradouro && (
                                <div className="text-[11px] leading-tight">{c.logradouro}</div>
                              )}
                              <div className="font-mono text-[10px] text-muted-foreground">
                                CEP {c.cep}
                              </div>
                              {c.ativo ? (
                                <div className="text-success text-[10px] flex items-center gap-1 mt-0.5 font-medium">
                                  <CheckCircle2 className="h-2.5 w-2.5" /> COM COBERTURA
                                  {c.tipo && ` · ${c.tipo}`}
                                  {c.vel && ` · ${c.vel} Mbps`}
                                </div>
                              ) : (
                                <div className="text-danger text-[10px] flex items-center gap-1 mt-0.5 font-medium">
                                  <XCircle className="h-2.5 w-2.5" /> SEM COBERTURA
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Status da cascata */}
        {cascataStatus && (
          <Card>
            <CardHeader className="pb-1 pt-3 px-4">
              <CardTitle className="text-xs">Status da consulta</CardTitle>
            </CardHeader>
            <CardContent className="pt-0 px-4 pb-3 flex flex-wrap gap-1">
              <StatusSelo status={(cascataStatus as any).receita} label="Receita" />
              <StatusSelo status={(cascataStatus as any).assertiva} label="Assertiva" />
              <StatusSelo status={(cascataStatus as any).operadora} label="Operadoras" />
              <StatusSelo status={(cascataStatus as any).cobertura} label="Cobertura" />
            </CardContent>
          </Card>
        )}

        {/* Ações */}
        <Card className="border-2 border-primary/30 bg-primary/5">
          <CardContent className="py-3 px-4 space-y-2">
            <div className="flex items-center gap-1.5 text-xs font-medium">
              <Sparkles className="h-3.5 w-3.5 text-primary" /> Ações
            </div>
            <Button onClick={handleCriarLead} disabled={criandoLead} size="sm" className="w-full gap-2">
              <UserPlus className="h-4 w-4" /> Virar lead agora
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleCopiar} className="flex-1 gap-1">
                <Copy className="h-3 w-3" /> Copiar
              </Button>
              {onReEnriquecer && (
                <Button variant="outline" size="sm" onClick={onReEnriquecer} className="flex-1 gap-1">
                  <RefreshCw className="h-3 w-3" /> Atualizar
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </aside>

      {/* ────────────── COLUNA DIREITA ────────────── */}
      <main className="lg:col-span-8 xl:col-span-9 space-y-3">
        {/* Empresa: Receita | Assertiva CNPJ */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* RECEITA FEDERAL */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2 flex-wrap">
                <Building2 className="h-4 w-4 text-info" />
                Empresa
                <FonteBadge fonte="receita" />
                {est.tipo && (
                  <Badge variant="outline" className="text-[9px] py-0 h-4">
                    {String(est.tipo).toUpperCase()}
                  </Badge>
                )}
                {est.situacao_cadastral && (
                  <Badge
                    variant="outline"
                    className={
                      'text-[9px] py-0 h-4 ' +
                      (est.situacao_cadastral === 'Ativa'
                        ? 'bg-success/10 text-success border-success/30'
                        : 'bg-danger/10 text-danger border-danger/30')
                    }
                  >
                    {est.situacao_cadastral.toUpperCase()}
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-xs">
              <div className="grid grid-cols-2 gap-2">
                <Field label="Fantasia" value={est.nome_fantasia} />
                <Field label="Natureza" value={r.natureza_juridica?.descricao} />
                <Field label="Porte" value={r.porte?.descricao} />
                <Field
                  label="Capital"
                  value={
                    r.capital_social
                      ? `R$ ${Number(r.capital_social).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                      : null
                  }
                />
                <Field label="Abertura" value={est.data_inicio_atividade} />
                <Field
                  label="Data situação"
                  value={est.data_situacao_cadastral}
                />
                <Field
                  label="Motivo situação"
                  value={est.motivo_situacao_cadastral?.descricao}
                />
                <Field label="Simples" value={r.simples?.simples === 'Sim' ? 'Sim' : null} />
                <Field
                  label="Opção Simples"
                  value={r.simples?.data_opcao_simples}
                />
                <Field label="MEI" value={r.simples?.mei === 'Sim' ? 'Sim' : null} />
                <Field
                  label="Opção MEI"
                  value={r.simples?.data_opcao_mei}
                />
                <Field
                  label="Qualif. Responsável"
                  value={r.qualificacao_do_responsavel?.descricao}
                />
                <Field
                  label="Resp. Federativo"
                  value={r.responsavel_federativo}
                  mono
                />
                {est.cnpj_raiz && (
                  <Field
                    label="CNPJ raiz"
                    value={est.cnpj_raiz}
                    mono
                  />
                )}
                {est.situacao_especial && (
                  <Field
                    label="Situação especial"
                    value={`${est.situacao_especial}${est.data_situacao_especial ? ` (${est.data_situacao_especial})` : ''}`}
                  />
                )}
                {r.atualizado_em && (
                  <Field label="Atualizado em" value={r.atualizado_em} />
                )}
              </div>

              <div className="border-t pt-2">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">CNAE Principal</div>
                <div className="text-xs">
                  <span className="font-mono">{est.atividade_principal?.subclasse || est.atividade_principal?.id}</span>{' '}
                  {est.atividade_principal?.descricao}
                </div>
              </div>

              {est.atividades_secundarias?.length > 0 && (
                <div className="border-t pt-2">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                    CNAEs secundários ({est.atividades_secundarias.length})
                  </div>
                  <div className="space-y-0.5 max-h-32 overflow-y-auto">
                    {est.atividades_secundarias.map((c: any, i: number) => (
                      <div key={i} className="text-[11px]">
                        <span className="font-mono text-muted-foreground">{c.subclasse || c.id}</span> {c.descricao}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {est.inscricoes_estaduais?.length > 0 && (
                <div className="border-t pt-2">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                    Inscrições Estaduais ({est.inscricoes_estaduais.length})
                  </div>
                  <div className="space-y-0.5">
                    {est.inscricoes_estaduais.map((ie: any, i: number) => (
                      <div key={i} className="text-[11px]">
                        <b>{ie.estado?.sigla || ie.estado}</b> {ie.inscricao_estadual} {ie.ativo ? '✓' : '✕'}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {est.inscricoes_suframa?.length > 0 && (
                <div className="border-t pt-2">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                    Inscrições Suframa ({est.inscricoes_suframa.length})
                  </div>
                  <div className="space-y-0.5">
                    {est.inscricoes_suframa.map((sf: any, i: number) => (
                      <div key={i} className="text-[11px]">
                        {sf.inscricao_suframa} {sf.ativo ? '✓' : '✕'}
                        {sf.data_inicio && ` · desde ${sf.data_inicio}`}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {est.regimes_tributarios?.length > 0 && (
                <div className="border-t pt-2">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                    Regimes Tributários ({est.regimes_tributarios.length})
                  </div>
                  <div className="space-y-0.5 text-[11px]">
                    {est.regimes_tributarios.map((rt: any, i: number) => (
                      <div key={i}>
                        {rt.regime || rt.descricao || rt}
                        {rt.ano && <span className="text-muted-foreground"> · ano {rt.ano}</span>}
                        {rt.forma_de_tributacao && (
                          <span className="text-muted-foreground"> · {rt.forma_de_tributacao}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {est.inscricoes_municipais?.length > 0 && (
                <div className="border-t pt-2">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                    Inscrições Municipais ({est.inscricoes_municipais.length})
                  </div>
                  <div className="space-y-0.5">
                    {est.inscricoes_municipais.map((im: any, i: number) => (
                      <div key={i} className="text-[11px]">
                        {im.inscricao_municipal} {im.ativo ? '✓' : '✕'}
                        {im.cidade?.nome && ` · ${im.cidade.nome}`}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Sócios diretamente da Receita Federal (CNPJ.ws comercial). */}
              {/* Complementa o quadro da Assertiva — útil quando há divergência. */}
              {r.socios?.length > 0 && (
                <div className="border-t pt-2">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1">
                    <Users className="h-3 w-3" /> Sócios na Receita ({r.socios.length})
                  </div>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {r.socios.map((soc: any, i: number) => (
                      <div key={i} className="text-[11px] bg-background rounded p-1.5">
                        <div className="font-medium">{soc.nome}</div>
                        <div className="text-[10px] text-muted-foreground">
                          {soc.cpf_cnpj_socio && (
                            <span className="font-mono">{soc.cpf_cnpj_socio}</span>
                          )}
                          {soc.qualificacao_socio?.descricao && ` · ${soc.qualificacao_socio.descricao}`}
                          {soc.tipo && ` · ${soc.tipo}`}
                          {soc.data_entrada && ` · entrada ${soc.data_entrada}`}
                          {soc.faixa_etaria && ` · ${soc.faixa_etaria}`}
                        </div>
                        {soc.representante_legal?.nome && (
                          <div className="text-[10px] text-muted-foreground italic">
                            Repr. legal: {soc.representante_legal.nome}
                            {soc.representante_legal.cpf_cnpj_socio && ` (${soc.representante_legal.cpf_cnpj_socio})`}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(est.logradouro || est.cep) && (
                <div className="border-t pt-2">
                  <div className="flex items-start gap-1.5">
                    <MapPin className="h-3 w-3 text-muted-foreground mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0 space-y-0.5">
                      <div>
                        {est.tipo_logradouro} {est.logradouro}
                        {est.numero && `, ${est.numero}`}
                        {est.complemento && ` - ${est.complemento}`}
                      </div>
                      <div className="text-muted-foreground">
                        {est.bairro && `${est.bairro} · `}
                        {est.cidade?.nome}/{est.estado?.sigla} · CEP {est.cep}
                      </div>
                      <div className="pt-1">
                        <CoberturaCepBadge cep={est.cep} coberturaPorCep={coberturaPorCep} />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {(est.email || est.ddd1 || est.ddd_fax) && (
                <div className="border-t pt-2 space-y-1">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                    Contato cadastrado na Receita
                  </div>
                  {est.email && (
                    <div className="flex items-center gap-1.5">
                      <Mail className="h-3 w-3 text-muted-foreground" />
                      <span className="text-xs">{est.email}</span>
                    </div>
                  )}
                  {est.ddd1 && est.telefone1 && (() => {
                    // Receita guarda celular antigo sem 9º dígito.
                    // Detecta e ajusta para exibição + cross-match operadora.
                    const tel = String(est.telefone1).replace(/\D/g, '');
                    const ddd = String(est.ddd1).replace(/\D/g, '');
                    let formatado = `(${ddd}) ${est.telefone1}`;
                    let normalizado55 = '';
                    let isCelular = false;
                    if (tel.length === 8 && ['6', '7', '8', '9'].includes(tel[0])) {
                      // Celular antigo — adiciona 9 na exibição e no normalizado
                      formatado = `(${ddd}) 9${tel.slice(0, 4)}-${tel.slice(4)}`;
                      normalizado55 = `55${ddd}9${tel}`;
                      isCelular = true;
                    } else if (tel.length === 9 && tel[0] === '9') {
                      formatado = `(${ddd}) ${tel.slice(0, 5)}-${tel.slice(5)}`;
                      normalizado55 = `55${ddd}${tel}`;
                      isCelular = true;
                    } else if (tel.length === 8) {
                      formatado = `(${ddd}) ${tel.slice(0, 4)}-${tel.slice(4)}`;
                      normalizado55 = `55${ddd}${tel}`;
                    }
                    // Cross-match com operadoras detectadas pela cascata
                    const op = (data?.operadoras || []).find((o: any) => {
                      const od = String(o['NÚMERO'] || (o as any).NUMERO || '').replace(/\D/g, '');
                      return od === normalizado55;
                    });
                    return (
                      <div className="rounded-md border-2 border-info/30 bg-info/5 p-2 flex items-center gap-2 flex-wrap">
                        <Phone className="h-3.5 w-3.5 text-info shrink-0" />
                        <span className="font-mono font-bold text-sm text-foreground">{formatado}</span>
                        {isCelular && (
                          <Badge variant="outline" className="text-[9px] py-0 h-4 bg-success/10 text-success border-success/30">
                            celular
                          </Badge>
                        )}
                        {op?.OPERADORA && (
                          <Badge variant="outline" className="text-[9px] py-0 h-4">
                            {String(op.OPERADORA).toUpperCase()}
                          </Badge>
                        )}
                        {tel.length === 8 && isCelular && (
                          <span className="text-[9px] text-warning" title="Receita Federal cadastra celulares antigos sem 9º dígito — sistema corrigiu para consulta">
                            +9 corrigido
                          </span>
                        )}
                      </div>
                    );
                  })()}
                  {est.ddd2 && est.telefone2 && (
                    <div className="flex items-center gap-1.5">
                      <Phone className="h-3 w-3 text-muted-foreground" />
                      <span className="font-mono text-xs">
                        ({est.ddd2}) {est.telefone2}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* ASSERTIVA CNPJ */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2 flex-wrap">
                <Database className="h-4 w-4 text-primary" />
                Empresa enriquecida
                <FonteBadge fonte="assertiva-cnpj" />
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-xs">
              {Object.keys(ac).length === 0 && !a.enderecos?.length && (
                <div className="text-muted-foreground py-2">Sem dados retornados pela Assertiva CNPJ.</div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <Field label="Razão Social" value={ac.razaoSocial} />
                <Field label="Fantasia" value={ac.nomeFantasia} />
                <Field label="Porte" value={ac.porteEmpresa} />
                <Field label="Natureza Jurídica" value={ac.naturezaJuridica} />
                <Field label="CNAE" value={ac.cnae} mono />
                <Field label="CNAE Grupo" value={ac.cnaeGrupo} />
                <Field
                  label="Capital Social"
                  value={ac.capitalSocial ? `R$ ${Number(ac.capitalSocial).toLocaleString('pt-BR')}` : null}
                />
                <Field label="Funcionários" value={ac.quantidadeFuncionarios} />
                <Field label="Abertura" value={ac.dataAbertura} />
                <Field label="Idade" value={ac.idadeEmpresa ? `${ac.idadeEmpresa} anos` : null} />
                <Field label="Situação" value={ac.situacaoCadastral} />
                {ac.cnaeDescricao && (
                  <div className="col-span-2">
                    <Field label="CNAE Descrição" value={ac.cnaeDescricao} />
                  </div>
                )}
                {ac.site && (
                  <div className="col-span-2 flex items-center gap-1.5">
                    <Globe className="h-3 w-3 text-muted-foreground" />
                    <a
                      href={ac.site.startsWith('http') ? ac.site : `http://${ac.site}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-primary hover:underline"
                    >
                      {ac.site}
                    </a>
                  </div>
                )}
              </div>

              {a.enderecos?.length > 0 && (
                <div className="border-t pt-2">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                    Endereços ({a.enderecos.length})
                  </div>
                  <div className="space-y-1.5">
                    {a.enderecos.map((end: any, i: number) => (
                      <div key={i} className="flex items-start gap-1.5 text-[11px] bg-muted/40 rounded p-1.5">
                        <MapPin className="h-3 w-3 text-muted-foreground mt-0.5 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div>
                            {end.logradouro}
                            {end.numero && `, ${end.numero}`}
                            {end.complemento && ` - ${end.complemento}`}
                          </div>
                          <div className="text-muted-foreground">
                            {end.bairro && `${end.bairro} · `}
                            {end.cidade}/{end.uf} · {end.cep}
                          </div>
                          <div className="pt-0.5">
                            <CoberturaCepBadge cep={end.cep} coberturaPorCep={coberturaPorCep} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {a.telefones && (Array.isArray(a.telefones) ? a.telefones.length > 0 : true) && (
                <div className="border-t pt-2">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                    Telefones (Assertiva CNPJ)
                  </div>
                  <div className="space-y-0.5">
                    {(Array.isArray(a.telefones)
                      ? a.telefones
                      : [...(a.telefones?.moveis || []), ...(a.telefones?.fixos || [])]
                    )
                      .slice(0, 10)
                      .map((t: any, i: number) => (
                        <div key={i} className="flex items-center gap-1.5 text-[11px]">
                          <Phone className="h-2.5 w-2.5 text-muted-foreground" />
                          <span className="font-mono">{t.numero || `(${t.ddd || ''}) ${t.telefone || ''}`}</span>
                          {t.operadora && (
                            <Badge variant="outline" className="text-[9px] py-0 h-3.5">
                              {String(t.operadora).toUpperCase()}
                            </Badge>
                          )}
                          {(t.whatsapp || t.aplicativos?.whatsApp) && (
                            <Badge variant="outline" className="text-[9px] py-0 h-3.5 bg-success/10 text-success">
                              WA
                            </Badge>
                          )}
                          {t.relacao && <span className="text-[9px] text-muted-foreground">({t.relacao})</span>}
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {a.emails?.length > 0 && (
                <div className="border-t pt-2">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">E-mails</div>
                  <div className="space-y-0.5">
                    {a.emails.slice(0, 5).map((e: any, i: number) => (
                      <div key={i} className="flex items-center gap-1.5 text-[11px]">
                        <Mail className="h-2.5 w-2.5 text-muted-foreground" />
                        <span>{e.email || e}</span>
                        {e.principal && (
                          <Badge variant="outline" className="text-[9px] py-0 h-3.5">
                            principal
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {a.redesSociais?.length > 0 && (
                <div className="border-t pt-2">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Redes sociais</div>
                  <div className="space-y-0.5">
                    {a.redesSociais.map((rs: any, i: number) => {
                      const url = rs.url || rs.perfil || rs.link || '';
                      return (
                        <div key={i} className="flex items-center gap-1.5 text-[11px]">
                          <Globe className="h-2.5 w-2.5 text-muted-foreground" />
                          {url && (
                            <a
                              href={url.startsWith('http') ? url : `https://${url}`}
                              target="_blank"
                              rel="noreferrer"
                              className="text-primary hover:underline truncate"
                            >
                              {url}
                            </a>
                          )}
                          {(rs.tipo || rs.rede) && (
                            <Badge variant="outline" className="text-[9px] py-0 h-3.5">
                              {rs.tipo || rs.rede}
                            </Badge>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Telefones consolidados — agrupados por origem com fallback */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 flex-wrap">
              <Phone className="h-4 w-4 text-success" />
              Telefones consolidados
              <FonteBadge fonte="operadora" />
              <span className="text-xs font-normal text-muted-foreground ml-auto">{totalTelefones} total</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {totalTelefones === 0 && (
              <div className="text-xs text-muted-foreground py-2">Nenhum telefone foi consultado.</div>
            )}
            {telefonesGrupos.empresa.length > 0 && (
              <TelefonesGrupo
                titulo="Da empresa"
                icon={<Building className="h-3 w-3" />}
                items={telefonesGrupos.empresa}
              />
            )}
            {telefonesGrupos.socios.length > 0 && (
              <TelefonesGrupo
                titulo="Dos sócios"
                icon={<User className="h-3 w-3" />}
                items={telefonesGrupos.socios}
                showDetalhe
              />
            )}
            {telefonesGrupos.crm.length > 0 && (
              <TelefonesGrupo
                titulo="Linhas no CRM"
                icon={<Database className="h-3 w-3" />}
                items={telefonesGrupos.crm}
              />
            )}
            {telefonesGrupos.semOrigem.length > 0 && (
              <TelefonesGrupo
                titulo={
                  telefonesGrupos.empresa.length === 0 &&
                  telefonesGrupos.socios.length === 0 &&
                  telefonesGrupos.crm.length === 0
                    ? 'Telefones'
                    : 'Outros telefones'
                }
                icon={<Phone className="h-3 w-3" />}
                items={telefonesGrupos.semOrigem}
              />
            )}
          </CardContent>
        </Card>

        {/* Sócios */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 flex-wrap">
              <Users className="h-4 w-4 text-primary" />
              Sócios
              <FonteBadge fonte="assertiva-cnpj" />
              <FonteBadge fonte="assertiva-cpf" />
              <span className="text-xs font-normal text-muted-foreground ml-auto">{sociosEnr.length} sócio(s)</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {sociosEnr.length === 0 ? (
              <div className="text-xs text-muted-foreground py-2">Nenhum sócio identificado pela Assertiva.</div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {sociosEnr.map((item, i) => (
                  <SocioCard
                    key={i}
                    item={item}
                    coberturaPorCep={coberturaPorCep}
                    telefonesOrigem={data?.telefones_origem || {}}
                    operadoras={data?.operadoras || []}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

// ── TelefonesGrupo ──────────────────────────────────────────────────────────
function TelefonesGrupo({
  titulo,
  icon,
  items,
  showDetalhe,
}: {
  titulo: string;
  icon: React.ReactNode;
  items: Array<{ numero: string; operadora: string; portabilidade: string; detalhe?: string }>;
  showDetalhe?: boolean;
}) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1">
        {icon} {titulo} ({items.length})
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-3">
        {items.map((t, i) => {
          const isClaro = ['CLARO', 'CLARO SA', 'CLARO BRASIL'].includes(String(t.operadora).toUpperCase());
          return (
            <div key={i} className="flex items-center gap-2 text-xs py-1 border-b last:border-b-0">
              <span className="font-mono text-[11px]">{t.numero}</span>
              {t.operadora && (
                <Badge
                  variant="outline"
                  className={cn(
                    'text-[10px] py-0 h-4',
                    isClaro ? 'border-muted' : 'border-warning/40 text-warning bg-warning/10'
                  )}
                >
                  {t.operadora}
                </Badge>
              )}
              {t.portabilidade && t.portabilidade !== '-' && (
                <span className="text-[9px] text-warning">port: {t.portabilidade}</span>
              )}
              {showDetalhe && t.detalhe && (
                <span className="text-[9px] text-muted-foreground truncate max-w-[120px]">{t.detalhe}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── SocioCard ──────────────────────────────────────────────────────────────
function SocioCard({
  item,
  coberturaPorCep,
  telefonesOrigem,
  operadoras,
}: {
  item: any;
  coberturaPorCep: Record<string, any> | null;
  telefonesOrigem?: Record<string, { origem: string; detalhe?: string }>;
  operadoras?: Array<{ NÚMERO?: string; OPERADORA?: string; 'DATA PORTABILIDADE'?: string }>;
}) {
  const s = item.socio_original || {};
  const e = item.enriquecimento || {};
  const dc = e.dadosCadastrais || {};
  const enderecos: any[] = e.enderecos || (e.endereco ? [e.endereco] : []);
  const tels: any[] = (() => {
    const arr = e.telefones || e.listaTelefones;
    if (Array.isArray(arr)) return arr;
    if (arr && typeof arr === 'object') {
      return [...(arr.moveis || []), ...(arr.fixos || [])];
    }
    return [];
  })();
  const emails: any[] = e.emails || e.listaEmails || [];
  const redes: any[] = e.redesSociais || [];
  const participacoes: any[] = e.participacoesEmpresas || e.participacoes || [];
  const cpf = (s.cpf || s.cnpj || s.documento || '').replace(/\D/g, '');
  const nome = dc.nome || s.nomeOuRazaoSocial || s.nome || s.nomeCompleto;
  const qual = s.qualificacao || s.cargo || '';
  const decisor = !!(qual && /administrador|diretor|s[oó]cio.?admin|presidente|ceo/i.test(qual));
  const temEnriquecimento = Object.keys(dc).length > 0 || enderecos.length > 0 || tels.length > 0;

  // ── Telefone principal do sócio ────────────────────────────────────────
  // Camadas: 1) telefone marcado como `principal: true` na Assertiva CPF
  //          2) primeiro celular (móvel) do enriquecimento
  //          3) cross-match em telefones_origem por origem='socio' + detalhe=nome
  // Mostra com badge da operadora detectada (se houver) + WhatsApp.
  const normalizarParaCompare = (raw: any): string => String(raw || '').replace(/\D/g, '');
  const normalizarPara55 = (raw: any): string => {
    const d = normalizarParaCompare(raw);
    if (!d) return '';
    if (d.length === 10 || d.length === 11) return `55${d}`;
    if ((d.length === 12 || d.length === 13) && d.startsWith('55')) return d;
    return d;
  };

  const telefonePrincipal = (() => {
    // (1) e (2): pega da própria Assertiva CPF
    const principalEnriq =
      tels.find((t: any) => t.principal === true) ||
      tels.find((t: any) => t.tipo === 'movel' || t.tipo === 'celular') ||
      tels[0];
    if (principalEnriq) {
      const numero = principalEnriq.numero || `${principalEnriq.ddd || ''}${principalEnriq.telefone || ''}`;
      return {
        numero: String(numero).trim(),
        operadora: principalEnriq.operadora || null,
        whatsapp: !!(principalEnriq.whatsapp || principalEnriq.aplicativos?.whatsApp),
        fonte: 'Assertiva CPF',
      };
    }
    // (3) cross-match: telefones com origem='socio' e detalhe casando com o nome do sócio
    if (telefonesOrigem && nome) {
      const nomeUpper = String(nome).toUpperCase();
      for (const [num55, info] of Object.entries(telefonesOrigem)) {
        if (info?.origem === 'socio' && info?.detalhe &&
            String(info.detalhe).toUpperCase().includes(nomeUpper.split(' ')[0])) {
          // Pega operadora detectada se houver
          const op = (operadoras || []).find(
            (o: any) => normalizarParaCompare(o['NÚMERO'] || o.NUMERO) === normalizarParaCompare(num55)
          );
          return {
            numero: num55,
            operadora: op?.OPERADORA || null,
            whatsapp: false,
            fonte: 'Cross-match',
          };
        }
      }
    }
    return null;
  })();

  const telPrincipalNorm = telefonePrincipal ? normalizarPara55(telefonePrincipal.numero) : '';

  // ── Telefones da EMPRESA cruzados com este sócio ───────────────────────
  // O número que está em est.ddd1+telefone1 da Receita aparece como
  // origem='empresa' no telefonesMap. Quando a empresa tem 1 só decisor
  // (ou este é o decisor principal), é provável que esse telefone seja
  // usado pelo sócio. Mostramos para o operador decidir se usa como
  // contato direto.
  const telefonesEmpresa: Array<{ numero: string; operadora: string | null; detalhe?: string }> = (() => {
    if (!telefonesOrigem) return [];
    const arr: Array<{ numero: string; operadora: string | null; detalhe?: string }> = [];
    for (const [num55, info] of Object.entries(telefonesOrigem)) {
      if (info?.origem !== 'empresa') continue;
      const op = (operadoras || []).find(
        (o: any) => normalizarParaCompare(o['NÚMERO'] || (o as any).NUMERO) === normalizarParaCompare(num55)
      );
      arr.push({
        numero: num55,
        operadora: op?.OPERADORA || null,
        detalhe: info.detalhe,
      });
    }
    return arr;
  })();

  const formatarTel55 = (t: string): string => {
    const d = t.replace(/\D/g, '');
    const semDdi = d.startsWith('55') && d.length >= 12 ? d.slice(2) : d;
    if (semDdi.length === 11) return `(${semDdi.slice(0, 2)}) ${semDdi.slice(2, 7)}-${semDdi.slice(7)}`;
    if (semDdi.length === 10) return `(${semDdi.slice(0, 2)}) ${semDdi.slice(2, 6)}-${semDdi.slice(6)}`;
    return t;
  };

  return (
    <div className="border rounded-lg p-3 bg-muted/20 space-y-2">
      {/* Cabeçalho */}
      <div className="flex items-start gap-2 flex-wrap">
        <User className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
        <div className="font-semibold text-sm flex items-center gap-1.5 flex-wrap min-w-0 flex-1">
          <span className="truncate">{nome || 'Sócio sem nome'}</span>
          {decisor && <Badge className="bg-warning text-[9px] py-0 h-4">DECISOR</Badge>}
          {dc.ppe && (
            <Badge variant="outline" className="bg-warning/10 text-warning text-[9px] py-0 h-4">
              PEP
            </Badge>
          )}
          {dc.obitoProvavel && (
            <Badge variant="outline" className="bg-danger/10 text-danger text-[9px] py-0 h-4">
              Óbito provável
            </Badge>
          )}
        </div>
      </div>

      {/* ── DESTAQUE: Telefone principal do sócio ───────────────────────── */}
      {telefonePrincipal && (
        <div className="rounded-md border-2 border-success/40 bg-success/5 p-2.5 flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1.5 shrink-0">
            <Phone className="h-4 w-4 text-success" />
            <span className="text-[10px] uppercase tracking-wider text-success font-semibold">
              Telefone principal
            </span>
          </div>
          <span className="font-mono font-bold text-sm text-foreground">
            {formatarTel55(telefonePrincipal.numero)}
          </span>
          {telefonePrincipal.operadora && (
            <Badge variant="outline" className="text-[9px] py-0 h-4">
              {String(telefonePrincipal.operadora).toUpperCase()}
            </Badge>
          )}
          {telefonePrincipal.whatsapp && (
            <Badge
              variant="outline"
              className="text-[9px] py-0 h-4 bg-success/10 text-success border-success/30"
            >
              WA
            </Badge>
          )}
          <span className="text-[9px] text-muted-foreground ml-auto">{telefonePrincipal.fonte}</span>
        </div>
      )}

      {/* ── Telefones da empresa (cruzamento Receita/Assertiva CNPJ) ─────── */}
      {telefonesEmpresa.length > 0 && (
        <div className="rounded-md border border-info/30 bg-info/5 p-2 space-y-1">
          <div className="text-[10px] uppercase tracking-wider text-info font-semibold flex items-center gap-1">
            <Building2 className="h-3 w-3" /> Telefones da empresa onde {nome?.split(' ')[0] || 'este sócio'} é sócio
          </div>
          <div className="space-y-0.5">
            {telefonesEmpresa.slice(0, 6).map((t, i) => (
              <div key={i} className="flex items-center gap-1.5 text-[11px]">
                <Phone className="h-3 w-3 text-muted-foreground shrink-0" />
                <span className="font-mono">{formatarTel55(t.numero)}</span>
                {t.operadora && (
                  <Badge variant="outline" className="text-[9px] py-0 h-4">
                    {String(t.operadora).toUpperCase()}
                  </Badge>
                )}
                {t.detalhe && (
                  <span className="text-[9px] text-muted-foreground ml-auto">{t.detalhe}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sociedade — Assertiva CNPJ (badge no header da seção) */}
      <div className="space-y-1.5">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
          Sociedade <FonteBadge fonte="assertiva-cnpj" />
        </div>
        <div className="grid grid-cols-2 gap-2 text-[11px]">
          {cpf && <Field label="CPF" value={formatarCPF(cpf)} mono />}
          {qual && <Field label="Qualificação" value={qual} />}
          {typeof s.participacao === 'number' && <Field label="Participação" value={`${s.participacao}%`} />}
          {s.dataEntrada && <Field label="Entrada" value={s.dataEntrada} />}
        </div>
      </div>

      {/* Dados pessoais — Assertiva CPF (badge no header da seção) */}
      {temEnriquecimento && Object.keys(dc).length > 0 && (
        <div className="border-t pt-2 space-y-1.5">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
            Dados pessoais <FonteBadge fonte="assertiva-cpf" />
          </div>
          <div className="grid grid-cols-2 gap-2 text-[11px]">
            {dc.idade && (
              <div className="flex items-center gap-1">
                <Calendar className="h-2.5 w-2.5 text-muted-foreground" />
                <span>
                  {dc.idade} anos {dc.sexo ? `· ${dc.sexo}` : ''}
                </span>
              </div>
            )}
            {dc.dataNascimento && <Field label="Nascimento" value={dc.dataNascimento} />}
            {dc.signo && <Field label="Signo" value={dc.signo} />}
            {dc.rg && <Field label="RG" value={`${dc.rg}${dc.ufRg ? ` (${dc.ufRg})` : ''}`} mono />}
            {dc.maeNome && <Field label="Mãe" value={dc.maeNome} />}
            {dc.maeCpf && <Field label="CPF da mãe" value={formatarCPF(dc.maeCpf)} mono />}
            {dc.situacaoCadastral && <Field label="Situação CPF" value={dc.situacaoCadastral} />}
            {dc.ppe_observacao && (
              <div className="col-span-2">
                <Field label="PEP" value={dc.ppe_observacao} />
              </div>
            )}
          </div>
        </div>
      )}

      {enderecos.length > 0 && (
        <div className="border-t pt-2 space-y-1">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
            Endereços ({enderecos.length}) <FonteBadge fonte="assertiva-cpf" />
          </div>
          {enderecos.map((end, i) => (
            <div key={i} className="flex items-start gap-1.5 text-[11px] bg-background rounded p-1.5">
              <MapPin className="h-3 w-3 text-muted-foreground mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div>
                  {end.logradouro}
                  {end.numero && `, ${end.numero}`}
                  {end.complemento && ` - ${end.complemento}`}
                </div>
                <div className="text-muted-foreground">
                  {end.bairro && `${end.bairro} · `}
                  {end.cidade}/{end.uf} · {end.cep}
                </div>
                <div className="pt-0.5">
                  <CoberturaCepBadge cep={end.cep} coberturaPorCep={coberturaPorCep} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tels.length > 0 && (
        <div className="border-t pt-2 space-y-1">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
            Outros telefones do sócio ({tels.length}) <FonteBadge fonte="assertiva-cpf" />
          </div>
          {tels.map((t: any, i: number) => {
            const numero = t.numero || `(${t.ddd || ''}) ${t.telefone || ''}`;
            const isWa = !!(t.whatsapp || t.aplicativos?.whatsApp);
            return (
              <div key={i} className="flex items-center gap-1.5 text-[11px] flex-wrap">
                <Phone className="h-3 w-3 text-muted-foreground shrink-0" />
                <span className="font-mono">{numero}</span>
                {t.operadora && (
                  <Badge variant="outline" className="text-[9px] py-0 h-4">
                    {String(t.operadora).toUpperCase()}
                  </Badge>
                )}
                {isWa && (
                  <Badge
                    variant="outline"
                    className="text-[9px] py-0 h-4 bg-success/10 text-success border-success/30"
                  >
                    WA
                  </Badge>
                )}
                {t.relacao && <span className="text-[9px] text-muted-foreground">({t.relacao})</span>}
                {t.naoPerturbe && (
                  <Badge variant="outline" className="text-[9px] py-0 h-4 bg-danger/10 text-danger">
                    DNP
                  </Badge>
                )}
                {t.hotphone && (
                  <Badge variant="outline" className="text-[9px] py-0 h-4 bg-warning/10 text-warning">
                    HOT
                  </Badge>
                )}
              </div>
            );
          })}
        </div>
      )}

      {emails.length > 0 && (
        <div className="border-t pt-2 space-y-0.5">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
            E-mails ({emails.length}) <FonteBadge fonte="assertiva-cpf" />
          </div>
          {emails.slice(0, 5).map((em: any, i: number) => (
            <div key={i} className="flex items-center gap-1.5 text-[11px]">
              <Mail className="h-3 w-3 text-muted-foreground" />
              <span>{em.email || em}</span>
              {em.principal && (
                <Badge variant="outline" className="text-[9px] py-0 h-4">
                  principal
                </Badge>
              )}
            </div>
          ))}
        </div>
      )}

      {redes.length > 0 && (
        <div className="border-t pt-2 space-y-0.5">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
            Redes <FonteBadge fonte="assertiva-cpf" />
          </div>
          {redes.map((rs: any, i: number) => {
            const url = rs.url || rs.perfil || rs.link || '';
            return (
              <div key={i} className="flex items-center gap-1.5 text-[11px]">
                <Globe className="h-3 w-3 text-muted-foreground" />
                {url && (
                  <a
                    href={url.startsWith('http') ? url : `https://${url}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary hover:underline truncate"
                  >
                    {url}
                  </a>
                )}
                {(rs.tipo || rs.rede) && (
                  <Badge variant="outline" className="text-[9px] py-0 h-4">
                    {rs.tipo || rs.rede}
                  </Badge>
                )}
              </div>
            );
          })}
        </div>
      )}

      {participacoes.length > 0 && (
        <div className="border-t pt-2 space-y-0.5">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
            Participa em ({participacoes.length}) <FonteBadge fonte="assertiva-cpf" />
          </div>
          {participacoes.slice(0, 5).map((p: any, i: number) => (
            <div key={i} className="text-[11px] flex items-start gap-1">
              <Briefcase className="h-3 w-3 text-muted-foreground mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{p.razaoSocial}</div>
                <div className="text-muted-foreground text-[10px]">
                  {p.cnpj && <span className="font-mono">{formatarCNPJ(String(p.cnpj).replace(/\D/g, ''))}</span>}
                  {p.qualificacao && ` · ${p.qualificacao}`}
                  {p.participacao && ` · ${p.participacao}%`}
                  {p.situacao && ` · ${p.situacao}`}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
